import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { openInGoogleMaps, formatCoordinates } from '../utils/locationTracker';
import './ReportsModule.css';

function ReportsModule({ technicians }) {
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Date range filter
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  
  const [selectedTechnician, setSelectedTechnician] = useState('all');
  const [selectedOutlet, setSelectedOutlet] = useState('all');
  const [selectedTerritory, setSelectedTerritory] = useState('all');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [selectedArea, setSelectedArea] = useState('all');

  useEffect(() => {
    loadAllServices();
  }, [dateRange.startDate, dateRange.endDate]);

  const loadAllServices = async () => {
    setLoading(true);
    try {
      const start = new Date(dateRange.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59, 999);

      const servicesRef = collection(db, 'serviceRecords');
      const q = query(
        servicesRef,
        where('createdAt', '>=', Timestamp.fromDate(start)),
        where('createdAt', '<=', Timestamp.fromDate(end)),
        orderBy('createdAt', 'desc'),
        limit(5000)
      );

      const snapshot = await getDocs(q);
      const servicesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAllServices(servicesList);
    } catch (error) {
      console.error('Error loading services:', error);
      showToast('❌ Error loading services');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredServices = () => {
    // Date range is now applied server-side in the query itself — every
    // record in allServices already falls within the selected range, so
    // there's nothing left to check here except the other filters.
    let filtered = allServices;

    if (selectedTechnician !== 'all') {
      filtered = filtered.filter(s => s.technicianId === selectedTechnician);
    }

    if (selectedOutlet !== 'all') {
      filtered = filtered.filter(s => s.outletName === selectedOutlet);
    }

    // Only applies to service records submitted after the org-structure
    // wiring — older records simply won't have these fields and are
    // excluded once one of these filters is actually used, same as any
    // other filter would exclude non-matching records.
    if (selectedTerritory !== 'all') {
      filtered = filtered.filter(s => s.territoryName === selectedTerritory);
    }
    if (selectedDivision !== 'all') {
      filtered = filtered.filter(s => s.divisionName === selectedDivision);
    }
    if (selectedArea !== 'all') {
      filtered = filtered.filter(s => s.areaName === selectedArea);
    }

    return filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    });
  };

  const exportToExcel = (data, filename) => {
    setExportLoading(true);
    
    try {
      // Create CSV headers
      const headers = [
        'Order Number',
        'Date',
        'Time',
        'Technician',
        'Outlet Name',
        'Equipment Type',
        'Serial Number',
        'Service Type',
        'Status',
        'Fault Description',
        'Parts Used',
        'GPS Latitude',
        'GPS Longitude',
        'GPS Accuracy (meters)',
        'Drink Types',
        'Record Book Available',
        'Cleaning Kit Available',
        'Diokem Available',
        'Notes',
        'Duration (if completed)'
      ];

      // Create CSV rows
      const rows = data.map(service => {
        const createdAt = service.createdAt?.toDate ? service.createdAt.toDate() : new Date(service.createdAt);
        const completedAt = service.completedAt?.toDate ? service.completedAt.toDate() : null;
        
        // Calculate duration if completed
        let duration = 'N/A';
        if (completedAt && createdAt) {
          const durationMinutes = Math.round((completedAt - createdAt) / 1000 / 60);
          duration = `${durationMinutes} minutes`;
        }

        // Items available
        const recordBook = service.itemsAvailable?.recordBook ? 'Yes' : 'No';
        const cleaningKit = service.itemsAvailable?.cleaningKit ? 'Yes' : 'No';
        const diokem = service.itemsAvailable?.diokem ? 'Yes' : 'No';

        return [
          service.orderNumber || 'N/A',
          createdAt.toLocaleDateString('en-KE'),
          createdAt.toLocaleTimeString('en-KE'),
          service.technicianName || 'N/A',
          service.outletName || 'N/A',
          service.equipmentType === 'keg_pump' ? 'Keg Pump' : 'Draft Machine',
          service.serialNumber || 'N/A',
          service.serviceType || 'N/A',
          service.status || 'N/A',
          (service.faultDescription || 'N/A').replace(/"/g, '""'), // Escape quotes
          (service.partsUsed || 'N/A').replace(/"/g, '""'),
          service.location?.latitude?.toFixed(6) || 'N/A',
          service.location?.longitude?.toFixed(6) || 'N/A',
          service.location?.accuracy?.toFixed(0) || 'N/A',
          service.drinkTypes?.join(', ') || 'N/A',
          recordBook,
          cleaningKit,
          diokem,
          (service.notes || 'N/A').replace(/"/g, '""'),
          duration
        ];
      });

      // Convert to CSV with proper escaping
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Add BOM for Excel UTF-8 support
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);

      showToast('✅ Report exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      showToast('❌ Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = () => {
    const filtered = getFilteredServices();
    
    if (filtered.length === 0) {
      showToast('⚠️ No services found in selected date range');
      return;
    }

    const filename = `DileX_All_Staff_Report_${dateRange.startDate}_to_${dateRange.endDate}`;
    exportToExcel(filtered, filename);
  };

  const handleExportTechnician = (techId, techName) => {
    const filtered = getFilteredServices().filter(s => s.technicianId === techId);
    
    if (filtered.length === 0) {
      showToast(`⚠️ No services found for ${techName} in selected date range`);
      return;
    }

    const filename = `DileX_${techName.replace(/\s+/g, '_')}_Report_${dateRange.startDate}_to_${dateRange.endDate}`;
    exportToExcel(filtered, filename);
  };

  const showToast = (message) => {
    const existingToasts = document.querySelectorAll('.toast-notification');
    existingToasts.forEach(toast => toast.remove());

    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.innerHTML = `
      <div class="toast-icon">📊</div>
      <div class="toast-message">${message}</div>
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  const filteredServices = getFilteredServices();

  // Distinct outlet names actually present in the loaded data — no extra
  // Firestore query needed, service records already carry outletName.
  const distinctOutlets = [...new Set(allServices.map(s => s.outletName).filter(Boolean))].sort();

  // Cascading: divisions/areas shown are only the ones actually present
  // within whatever's already selected above them — same pattern as the
  // Outlet Registration and Org Structure pickers.
  const distinctTerritories = [...new Set(allServices.map(s => s.territoryName).filter(Boolean))].sort();
  const distinctDivisions = [...new Set(
    allServices
      .filter(s => selectedTerritory === 'all' || s.territoryName === selectedTerritory)
      .map(s => s.divisionName)
      .filter(Boolean)
  )].sort();
  const distinctAreas = [...new Set(
    allServices
      .filter(s => selectedDivision === 'all' || s.divisionName === selectedDivision)
      .map(s => s.areaName)
      .filter(Boolean)
  )].sort();

  // Calculate stats for filtered data
  const stats = {
    total: filteredServices.length,
    completed: filteredServices.filter(s => s.status === 'completed').length,
    inProgress: filteredServices.filter(s => s.status === 'in_progress').length,
    pending: filteredServices.filter(s => s.status === 'pending').length
  };

  // Group services by technician
  const technicianServices = {};
  filteredServices.forEach(service => {
    if (!technicianServices[service.technicianId]) {
      technicianServices[service.technicianId] = {
        id: service.technicianId,
        name: service.technicianName,
        services: []
      };
    }
    technicianServices[service.technicianId].services.push(service);
  });

  return (
    <div className="reports-module">
      <div className="reports-header">
        <div className="reports-title">
          <h2>📄 Export Reports</h2>
          <p>Generate detailed service reports with GPS coordinates and timestamps</p>
        </div>
      </div>

      {/* Date Range & Filter Controls */}
      <div className="reports-controls">
        <div className="date-range-picker">
          <div className="date-input-group">
            <label>Start Date</label>
            <div className="date-input date-input-shell">
              <span className="date-input-display">
                {dateRange.startDate
                  ? (() => {
                      const [yyyy, mm, dd] = dateRange.startDate.split('-');
                      return `${mm} / ${dd} / ${yyyy}`;
                    })()
                  : 'Select date'}
              </span>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="date-input-hidden"
              />
            </div>
          </div>
          
          <div className="date-input-group">
            <label>End Date</label>
            <div className="date-input date-input-shell">
              <span className="date-input-display">
                {dateRange.endDate
                  ? (() => {
                      const [yyyy, mm, dd] = dateRange.endDate.split('-');
                      return `${mm} / ${dd} / ${yyyy}`;
                    })()
                  : 'Select date'}
              </span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="date-input-hidden"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div className="date-input-group">
            <label>Technician</label>
            <select
              value={selectedTechnician}
              onChange={(e) => setSelectedTechnician(e.target.value)}
              className="tech-select"
            >
              <option value="all">All Technicians</option>
              {technicians.map(tech => (
                <option key={tech.id} value={tech.id}>{tech.name}</option>
              ))}
            </select>
          </div>

          <div className="date-input-group">
            <label>Outlet</label>
            <select
              value={selectedOutlet}
              onChange={(e) => setSelectedOutlet(e.target.value)}
              className="tech-select"
            >
              <option value="all">All Outlets</option>
              {distinctOutlets.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="date-input-group">
            <label>Territory</label>
            <select
              value={selectedTerritory}
              onChange={(e) => { setSelectedTerritory(e.target.value); setSelectedDivision('all'); setSelectedArea('all'); }}
              className="tech-select"
            >
              <option value="all">All Territories</option>
              {distinctTerritories.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="date-input-group">
            <label>Division</label>
            <select
              value={selectedDivision}
              onChange={(e) => { setSelectedDivision(e.target.value); setSelectedArea('all'); }}
              className="tech-select"
            >
              <option value="all">All Divisions</option>
              {distinctDivisions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="date-input-group">
            <label>Area</label>
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="tech-select"
            >
              <option value="all">All Areas</option>
              {distinctAreas.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={loadAllServices}
            className="refresh-btn"
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Quick Date Filters */}
        <div className="quick-filters">
          <button
            onClick={() => {
              const today = new Date();
              setDateRange({
                startDate: today.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
              });
            }}
            className="quick-filter-btn"
          >
            Today
          </button>
          
          <button
            onClick={() => {
              const today = new Date();
              const lastWeek = new Date(today.setDate(today.getDate() - 7));
              setDateRange({
                startDate: lastWeek.toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0]
              });
            }}
            className="quick-filter-btn"
          >
            Last 7 Days
          </button>
          
          <button
            onClick={() => {
              const today = new Date();
              const lastMonth = new Date(today.setDate(today.getDate() - 30));
              setDateRange({
                startDate: lastMonth.toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0]
              });
            }}
            className="quick-filter-btn"
          >
            Last 30 Days
          </button>
          
          <button
            onClick={() => {
              const today = new Date();
              const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
              setDateRange({
                startDate: startOfMonth.toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0]
              });
            }}
            className="quick-filter-btn"
          >
            This Month
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="reports-stats">
        <div className="stat-box">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-text">Total Services</div>
        </div>
        <div className="stat-box success">
          <div className="stat-number">{stats.completed}</div>
          <div className="stat-text">Completed</div>
        </div>
        <div className="stat-box warning">
          <div className="stat-number">{stats.inProgress}</div>
          <div className="stat-text">In Progress</div>
        </div>
        <div className="stat-box info">
          <div className="stat-number">{stats.pending}</div>
          <div className="stat-text">Pending</div>
        </div>
      </div>

      {/* Export All Button */}
      <div className="export-all-section">
        <button
          onClick={handleExportAll}
          disabled={exportLoading || filteredServices.length === 0}
          className="export-all-btn"
        >
          {exportLoading ? (
            <>
              <span className="spinner-small"></span>
              Exporting...
            </>
          ) : (
            <>
              📊 Export All Services ({filteredServices.length})
            </>
          )}
        </button>
        <p className="export-hint">
          Exports include: Order #, Timestamps, GPS Coordinates, Parts Used, and more
        </p>
      </div>

      {/* Individual Technician Exports */}
      <div className="technician-exports-section">
        <h3>Export by Technician</h3>
        
        {loading ? (
          <div className="loading-state-small">
            <div className="spinner"></div>
            <p>Loading technician data...</p>
          </div>
        ) : (
          <div className="technician-export-grid">
            {technicians.map(tech => {
              const techServices = filteredServices.filter(s => s.technicianId === tech.id);
              const completed = techServices.filter(s => s.status === 'completed').length;
              
              return (
                <div key={tech.id} className="tech-export-card">
                  <div className="tech-export-header">
                    <div className="tech-avatar-small">
                      {tech.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="tech-export-info">
                      <h4>{tech.name}</h4>
                      <p>{tech.phone}</p>
                    </div>
                  </div>
                  
                  <div className="tech-export-stats">
                    <div className="mini-stat">
                      <span className="mini-stat-value">{techServices.length}</span>
                      <span className="mini-stat-label">Total</span>
                    </div>
                    <div className="mini-stat">
                      <span className="mini-stat-value success-color">{completed}</span>
                      <span className="mini-stat-label">Done</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleExportTechnician(tech.id, tech.name)}
                    disabled={exportLoading || techServices.length === 0}
                    className="tech-export-btn"
                  >
                    {techServices.length === 0 ? (
                      '📭 No Services'
                    ) : (
                      <>📄 Export ({techServices.length})</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Services Preview */}
      {filteredServices.length > 0 && (
        <div className="recent-services-preview">
          <h3>Preview: Recent Services in Range</h3>
          <div className="services-preview-list">
            {filteredServices.slice(0, 5).map(service => {
              const createdAt = service.createdAt?.toDate ? service.createdAt.toDate() : new Date(service.createdAt);
              
              return (
                <div key={service.id} className="preview-service-item">
                  <div className="preview-header">
                    <span className="preview-order">#{service.orderNumber}</span>
                    <span className="preview-date">
                      {createdAt.toLocaleDateString('en-KE')} {createdAt.toLocaleTimeString('en-KE')}
                    </span>
                  </div>
                  <div className="preview-body">
                    <strong>{service.outletName}</strong> • {service.technicianName}
                  </div>
                  {service.location && (
                    <div className="preview-location">
                      <span className="location-text">
                        📍 {formatCoordinates(service.location.latitude, service.location.longitude)}
                      </span>
                      <button
                        className="preview-navigate-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openInGoogleMaps(
                            service.location.latitude,
                            service.location.longitude,
                            `${service.outletName} - ${service.orderNumber}`
                          );
                        }}
                        title="Open in Google Maps"
                      >
                        🗺️
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredServices.length > 5 && (
              <p className="preview-more">
                ...and {filteredServices.length - 5} more services
              </p>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredServices.length === 0 && (
        <div className="empty-state-reports">
          <div className="empty-icon">📭</div>
          <h3>No Services Found</h3>
          <p>Try adjusting your date range or filters</p>
        </div>
      )}
    </div>
  );
}

export default ReportsModule;
